const LOCAL_STORAGE_WISHLIST_KEY = 'shopify-wishlist';
const LOCAL_STORAGE_DELIMITER = ',';
const BUTTON_ACTIVE_CLASS = 'is-wishlist-active';
const GRID_LOADED_CLASS = 'loaded';

const selectors = {
  button: '[button-wishlist]',
  grid: '[grid-wishlist]',
  // Use full product card wrapper so we include media + content
  productCard: '.product-card-wrapper',
};

const sanitizeWishlistArray = (input) => {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const cleaned = [];
  input.forEach((item) => {
    const handle = (item || '').toString().trim();
    if (!handle || seen.has(handle)) return;
    seen.add(handle);
    cleaned.push(handle);
  });
  return cleaned;
};

document.addEventListener('DOMContentLoaded', () => {
  initButtons();
  initGrid();
  bindDelegatedClick();
  observeDomForWishlistButtons();
  updateWishlistCount();
});

document.addEventListener('shopify-wishlist:updated', (event) => {
  // Keep wishlist grid in sync and refresh button active states everywhere
  initGrid();
  initButtonsState();
  updateWishlistCount();
});

document.addEventListener('shopify-wishlist:init-product-grid', (event) => {
  // console.log('🫡', event.detail.wishlist);
});

document.addEventListener('shopify-wishlist:init-buttons', (event) => {
  // console.log('🫡', event.detail.wishlist);
});

const fetchProductCardHTML = async (handle) => {
  const productTileTemplateUrl = `/products/${encodeURIComponent(handle)}?section_id=wishlist-card`;
  try {
    const res = await fetch(productTileTemplateUrl, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    const wrapper = document.createElement('div');
    wrapper.innerHTML = text;
    const productCard = wrapper.querySelector(selectors.productCard);
    if (!productCard) throw new Error('Product card markup missing');
    productCard.setAttribute('data-wishlist-handle', handle);
    const productId =
      productCard.getAttribute('data-product-id') ||
      (productCard.dataset && productCard.dataset.productId) ||
      (productCard.querySelector('[data-product-id]')?.getAttribute('data-product-id')) ||
      null;
    const canonicalHandle =
      productCard.getAttribute('data-product-handle') ||
      (productCard.dataset && productCard.dataset.productHandle) ||
      handle;

    return {
      handle,
      canonicalHandle,
      productId,
      markup: productCard.outerHTML
    };
  } catch (err) {
    console.error(`[Shopify Wishlist] Failed to load content for handle: ${handle}`, err);
    return null;
  }
};

const setupGrid = async (grid) => {
  const wishlist = getWishlist();
  const responses = await Promise.all(wishlist.map(fetchProductCardHTML));
  const fragment = document.createDocumentFragment();
  const dedupeSet = new Set();
  responses.forEach((entry) => {
    if (!entry || !entry.markup) {
      return;
    }
    const dedupeKey = entry.handle;
    if (dedupeSet.has(dedupeKey)) return;
    dedupeSet.add(dedupeKey);
    const markup = entry.markup;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = markup;
    const card = wrapper.firstElementChild;
    if (!card) return;
    fragment.appendChild(card);
  });
  grid.innerHTML = '';
  grid.appendChild(fragment);
  try {
    const debugCards = Array.from(grid.querySelectorAll(selectors.productCard)).map((card) => ({
      wishlistHandle: card.getAttribute('data-wishlist-handle'),
      productHandle: card.getAttribute('data-product-handle'),
    }));
    console.debug('[Wishlist] cards appended', debugCards);
  } catch (_) {}
  grid.classList.add(GRID_LOADED_CLASS);
  initButtons();

  const event = new CustomEvent('shopify-wishlist:init-product-grid', {
    detail: { wishlist: wishlist }
  });
  document.dispatchEvent(event);
};

const setButtonState = (button, isActive) => {
  // Keep both classes for backward compatibility with existing CSS
  button.classList.toggle(BUTTON_ACTIVE_CLASS, !!isActive);
  button.classList.toggle('active', !!isActive);
};

const setupButtons = (buttons) => {
  // Only ensure correct active state; clicks handled via delegated listener
  buttons.forEach((button) => {
    const productHandle = button.dataset.productHandle || false;
    if (!productHandle) {
      console.error('[Shopify Wishlist] Missing `data-product-handle` attribute.');
      return;
    }
    setButtonState(button, wishlistContains(productHandle));
  });
};

const initGrid = () => {
  const grid = document.querySelector(selectors.grid) || false;
  if (grid) setupGrid(grid);
};

const initButtons = () => {
  const buttons = document.querySelectorAll(selectors.button) || [];
  if (buttons.length) setupButtons(buttons);
  const event = new CustomEvent('shopify-wishlist:init-buttons', {
    detail: { wishlist: getWishlist() }
  });
  document.dispatchEvent(event);
};

const initButtonsState = () => {
  const buttons = document.querySelectorAll(selectors.button) || [];
  buttons.forEach((btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    setButtonState(btn, wishlistContains(handle));
  });
};

const bindDelegatedClick = () => {
  document.addEventListener('click', (e) => {
    const button = e.target.closest(selectors.button);
    if (!button) return;
    const handle = button.dataset.productHandle;
    if (!handle) return console.error('[Shopify Wishlist] Missing `data-product-handle` on clicked button.');
    updateWishlist(handle);
    const active = wishlistContains(handle);
    // Sync all matching buttons across the page immediately, without relying on CSS.escape
    document.querySelectorAll(selectors.button).forEach((btn) => {
      if (btn.dataset.productHandle === handle) setButtonState(btn, active);
    });
  });
  document.addEventListener('click', (e) => {
    const clearBtn = e.target.closest('[data-wishlist-clear]');
    if (!clearBtn) return;
    resetWishlist();
    initGrid();
    initButtonsState();
    updateWishlistCount();
    showWishlistToast('Wishlist cleared');
  });
};

const observeDomForWishlistButtons = () => {
  // Debounce to avoid excessive recalcs during batch DOM updates
  let scheduled = false;
  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      initButtonsState();
    });
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        const added = Array.from(m.addedNodes || []);
        if (!added.length) continue;
        for (const node of added) {
          if (!(node instanceof Element)) continue;
          if (node.matches && node.matches(selectors.button)) { scheduleSync(); break; }
          if (node.querySelector && node.querySelector(selectors.button)) { scheduleSync(); break; }
        }
      }
    }
  });

  observer.observe(document.documentElement || document.body, {
    childList: true,
    subtree: true
  });
};

const getWishlist = () => {
  const wishlist = localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY) || false;
  if (!wishlist) return [];
  const entries = wishlist.split(LOCAL_STORAGE_DELIMITER);
  return sanitizeWishlistArray(entries);
};

const setWishlist = (array) => {
  const cleanedArray = sanitizeWishlistArray(array);
  const wishlist = cleanedArray.join(LOCAL_STORAGE_DELIMITER);
  if (cleanedArray.length) localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, wishlist);
  else localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);

  const event = new CustomEvent('shopify-wishlist:updated', {
    detail: { wishlist: cleanedArray }
  });
  document.dispatchEvent(event);

  return wishlist;
};

const updateWishlist = (handle) => {
  const normalizedHandle = (handle || '').toString().trim();
  if (!normalizedHandle) return setWishlist(getWishlist());
  const wishlist = getWishlist();
  const indexInWishlist = wishlist.indexOf(normalizedHandle);
  const adding = indexInWishlist === -1;
  if (adding) wishlist.push(normalizedHandle);
  else wishlist.splice(indexInWishlist, 1);
  const result = setWishlist(wishlist);
  showWishlistToast(adding ? 'Added to bookmarks' : 'Removed from bookmarks');
  return result;
};

const wishlistContains = (handle) => {
  const wishlist = getWishlist();
  return wishlist.includes(handle);
};

const resetWishlist = () => {
  return setWishlist([]);
};

// --- UI helpers: toast + badge count ---
let wishlistToastStyleInjected = false;
function injectWishlistToastStyle() {
  if (wishlistToastStyleInjected) return;
  const css = `
    .wishlist-toast { position: fixed; left: 50%; bottom: 16px; transform: translateX(-50%); background: rgba(0,0,0,.85); color: #fff; padding: 10px 14px; border-radius: 20px; font-size: 14px; z-index: 9999; opacity: 0; transition: opacity .2s ease, transform .2s ease; pointer-events: none; white-space: nowrap; }
    .wishlist-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
  `;
  const style = document.createElement('style');
  style.type = 'text/css';
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
  wishlistToastStyleInjected = true;
}

let wishlistToastTimer = null;
function showWishlistToast(message) {
  injectWishlistToastStyle();
  let toast = document.querySelector('.wishlist-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  // show
  requestAnimationFrame(() => toast.classList.add('show'));
  // hide after delay
  clearTimeout(wishlistToastTimer);
  wishlistToastTimer = setTimeout(() => {
    toast.classList.remove('show');
  }, 1400);
}

function updateWishlistCount() {
  try {
    const count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      el.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    });
    document.querySelectorAll('[data-wishlist-link]').forEach((link) => {
      link.classList.toggle('is-active', count > 0);
    });
  } catch (e) {}
}
